/**
 * One fetch for the whole console, and the one place that notices a session ending.
 *
 * Kept apart from `client.ts` so both it and `session.ts` can use it without importing each
 * other. It holds no base URL: callers already build their own.
 */

/** Told when the control plane says the session is gone, so the gate can put the login back. */
const listeners = new Set<() => void>();

export const onSignedOut = (listener: () => void): (() => void) => {
  listeners.add(listener);

  return () => listeners.delete(listener);
};

let signedOut = false;

const announceSignedOut = (): void => {
  if (signedOut) return;
  signedOut = true;
  for (const listener of listeners) listener();
};

export const clearSignedOut = (): void => {
  signedOut = false;
};

/**
 * One fetch for the whole console.
 *
 * A 401 means the session ended — expired, signed out in another tab, or the account disabled —
 * and every caller would otherwise render it as its own kind of failure: an empty list here, a
 * red banner there. Announcing it once puts the login screen back instead.
 */
export const apiFetch = async (input: string, init: RequestInit = {}): Promise<Response> => {
  const response = await fetch(input, { ...init, credentials: 'include' });
  if (response.status === 401) announceSignedOut();

  return response;
};

